import { useEffect, useRef, type ReactNode } from 'react';
import { gsap, reducedMotion, reveal, SceneVideo, useSectionMounted } from './core';
import type { Scene } from './media';

/**
 * Full-bleed cinematic video band. The frame scales open as it enters
 * (Apple-style), caption drifts with parallax.
 */
export default function Interlude({
  scene,
  title,
  sub,
  ticker,
  label,
}: {
  scene: Scene;
  title: ReactNode;
  sub?: string;
  ticker?: string[];
  label: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  useSectionMounted();

  useEffect(() => {
    if (reducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.frame',
        { scale: 0.86, borderRadius: 48, opacity: 0.65 },
        {
          scale: 1,
          borderRadius: 0,
          opacity: 1,
          ease: 'none',
          scrollTrigger: { trigger: root.current, start: 'top 90%', end: 'top 12%', scrub: true },
        }
      );
      reveal(
        '.caption > *',
        { y: 70, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, stagger: 0.14, duration: 1.1, ease: 'power3.out' },
        { trigger: root.current, start: 'top 45%' }
      );
      gsap.to('.caption', {
        yPercent: -40,
        ease: 'none',
        scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true },
      });
      if (ticker?.length) {
        reveal(
          '.cine-dest-ticker span',
          { y: 30, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, stagger: 0.08, duration: 0.8, ease: 'power2.out' },
          { trigger: root.current, start: 'top 30%' }
        );
      }
    }, root);
    return () => ctx.revert();
  }, [ticker]);

  return (
    <div className="cine-interlude" ref={root} role="region" aria-label={label}>
      <div className="frame">
        <SceneVideo scene={scene} className="cine-video-fill" />
        <div className="shade" />
      </div>
      <div className="caption">
        <h2>{title}</h2>
        {sub && <p>{sub}</p>}
      </div>
      {ticker && (
        <div className="cine-dest-ticker" aria-hidden>
          {ticker.map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}
